import { FONT_WEIGHT } from '@app/styles/themes/constants';
import styled from 'styled-components';

export const NAVY = '#1a295a';
const BORDER = '#e3e6ee';

export const Container = styled.div<{ $flushTop?: boolean }>`
  direction: rtl; /* Hebrew is right-to-left */
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 -16px;
  padding: ${({ $flushTop }) => ($flushTop ? '0px' : '44px')} 24px;
`;

export const GroupTitle = styled.h3`
  font-size: 20px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.extremeBold};
  margin: 24px 0 8px;
`;

export const Section = styled.div``;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const IntroLine = styled.p`
  font-size: 18px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.5;
  margin: 0;
  white-space: pre-line;
  word-break: break-word;
`;

export const IntroLabel = styled.span`
  font-weight: ${FONT_WEIGHT.extremeBold};
`;

export const IntroDivider = styled.hr`
  border: none;
  border-top: 1px solid ${BORDER};
  margin: 0 -24px;
`;

export const SectionDivider = styled.hr`
  border: none;
  border-top: 2px dashed rgba(0, 0, 0, 0.5);
  margin: 0 -24px;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
`;

const BADGE_ACCENT = '#ce6239';
const BADGE_BG = '#fdf3ef';

export const SectionNumber = styled.span`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${BADGE_BG};
  border: 1.5px solid ${BADGE_ACCENT};
  color: ${BADGE_ACCENT};
  font-size: 20px;
  font-weight: ${FONT_WEIGHT.medium};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SectionTitle = styled.h4`
  font-size: 20px;
  line-height: 31.43px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.bold};
  margin: 0;
`;

/**
 * The caller sends whatever icon it has, so the box is fixed and the image is
 * fitted inside it — a 1000px artwork lands at the same 44px as a 24px glyph,
 * uncropped and on the title's baseline.
 */
export const SectionIcon = styled.img`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  object-fit: contain;
  object-position: center;
`;

export const Description = styled.p`
  font-size: 14px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.light};
  line-height: 1.5;
  margin: 0 0 12px;
  white-space: pre-line;
`;

const PREFERRED_COLOR = '#1e7f4f';
const PREFERRED_BG = '#eef7f1';
const PREFERRED_BORDER = '#bfddcb';
export const LESS_GOOD_COLOR = '#b3261e';
const LESS_GOOD_BG = '#fcf1f0';
const LESS_GOOD_BORDER = '#edc9c5';
const CARD_BORDER = 'rgba(0, 0, 0, 0.1)';
const SOFT_BORDER = 'rgba(0, 0, 0, 0.06)';

export const Card = styled.div<{ $preferred?: boolean }>`
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${({ $preferred }) => ($preferred ? PREFERRED_BORDER : LESS_GOOD_BORDER)};
  background-color: #fff;
  overflow: hidden;
`;

export const BrandNote = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px 0;
  background-color: ${PREFERRED_BG};

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const BrandText = styled.p`
  flex: 1;
  min-width: 0;
  font-size: 19px;
  line-height: 28px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.medium};
  margin: 0;
  white-space: pre-line;
  word-break: break-word;

  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

export const BarozLogo = styled.img`
  display: block;
  flex: none;
  height: 28px;
  width: auto;
`;

export const CardHeader = styled.div<{ $preferred?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  background-color: ${({ $preferred }) => ($preferred ? PREFERRED_BG : LESS_GOOD_BG)};

  @media screen and (max-width: 768px) {
    flex-wrap: nowrap;
    gap: 12px;
  }
`;

// Continues the tinted header, so CardHeader's 20px bottom padding is the gap above it.
export const HeaderDescription = styled.div<{ $preferred?: boolean }>`
  padding: 0 24px 20px;
  background-color: ${({ $preferred }) => ($preferred ? PREFERRED_BG : LESS_GOOD_BG)};
`;

export const HeaderDescriptionText = styled.p`
  margin: 0;
  font-size: 19px;
  line-height: 28px;
  color: ${NAVY};
  white-space: pre-line;
  word-break: break-word;
`;

export const Company = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

export const CompanyChip = styled.span<{ $preferred?: boolean }>`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${CARD_BORDER};
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${({ $preferred }) => ($preferred ? PREFERRED_COLOR : LESS_GOOD_COLOR)};
`;

export const CompanyName = styled.span`
  font-size: 20px;
  line-height: 31.43px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.bold};
`;

/** The logo sits directly on the card header's tint — no panel behind it. */
export const CompanyLogoBox = styled.span`
  flex: none;
  height: 48px;
  display: flex;
  align-items: center;

  /* Gives way to the verdict pill, which must stay whole beside it on a phone. */
  @media screen and (max-width: 768px) {
    flex: 0 1 auto;
    min-width: 0;
    height: 44px;
  }
`;

export const CompanyLogo = styled.img`
  display: block;
  height: 30px;
  width: auto;
  max-width: 140px;
  object-fit: contain;

  @media screen and (max-width: 768px) {
    height: 26px;
    max-width: 100%;
  }
`;

export const Verdict = styled.span<{ $preferred?: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: ${FONT_WEIGHT.medium};
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    gap: 5px;
    padding: 5px 10px;
    font-size: 13px;
  }

  ${({ $preferred }) =>
    $preferred
      ? `color: #fff; background-color: ${PREFERRED_COLOR};`
      : `color: ${LESS_GOOD_COLOR}; background-color: #fff; border: 1px solid #e6b3ad;`}
`;

export const OfferLine = styled.div<{ $preferred?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid ${SOFT_BORDER};

  svg {
    flex: none;
    margin-top: 3px;
    color: ${({ $preferred }) => ($preferred ? PREFERRED_COLOR : LESS_GOOD_COLOR)};
  }
`;

export const OfferLineContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const OfferLineText = styled.p`
  font-size: 18px;
  line-height: 28px;
  color: #404040;
  margin: 0;
  white-space: pre-line;
  word-break: break-word;
`;

export const ImageBlock = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  border: 1px solid ${BORDER};
`;

/**
 * A PDF is linked rather than rendered. It sits in a `BlockRow` like a text line
 * so it shares their left edge, and is a button because the signed URL is only
 * fetched once it is clicked.
 */
export const FileLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 18px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.5;
  text-align: start;
  text-decoration: underline;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${NAVY};
  }
`;

export const Empty = styled.p`
  font-size: 14px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.light};
  margin: 0;
`;
